/**
 * Role-Protected Route Component
 * 
 * Enforces role-based access control for routes
 * Redirects unauthorized users to appropriate pages
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import routes from '../config/routes';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';

/**
 * RoleProtectedRoute component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Components to render if authorized
 * @param {string[]} props.allowedRoles - Array of roles that can access this route
 * @param {string} props.redirectTo - Path to redirect unauthorized users (optional)
 * @param {boolean} props.requireAuth - Whether authentication is required (default true)
 */
export default function RoleProtectedRoute({ 
  children, 
  allowedRoles = [], 
  redirectTo = null,
  requireAuth = true 
}) {
  const { user, isLoading, isAuthenticated } = useEnterpriseAuth();

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check authentication if required
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={routes.login()} replace />;
  }

  // Check role authorization
  if (allowedRoles.length > 0) {
    const userRole = user?.role || '';
    
    // ADMIN can access everything
    if (userRole === 'ADMIN') {
      return <>{children}</>;
    }

    // Check if user has one of the allowed roles
    if (!allowedRoles.includes(userRole)) {
      // Determine redirect based on user role
      if (redirectTo) {
        return <Navigate to={redirectTo} replace />;
      }

      // Default redirects based on role type
      const clinicalRoles = ['PHYSICIAN', 'NURSE', 'RESIDENT', 'SURGEON', 'ANESTHESIOLOGIST', 'CLINICIAN', 'TECH'];
      const adminRoles = ['ADMIN', 'HOSPITAL_ADMIN', 'ASC_ADMIN', 'BUSINESS_ADMIN'];

      if (clinicalRoles.includes(userRole)) {
        return <Navigate to="/clinical/analytics" replace />;
      } else if (adminRoles.includes(userRole)) {
        return <Navigate to="/admin/analytics" replace />;
      }

      // If no appropriate redirect, show unauthorized message
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="error">
            <Typography variant="h6">Access Denied</Typography>
            <Typography variant="body2">
              You do not have permission to access this page. 
              Required roles: {allowedRoles.join(', ')}
            </Typography>
          </Alert>
        </Box>
      );
    }
  }

  // User is authorized
  return <>{children}</>;
}

/**
 * Helper function to determine user's primary dashboard
 * @param {Object} user - User object with role
 * @returns {string} Path to appropriate dashboard
 */
export function getUserDashboardPath(user) {
  if (!user) return routes.login();

  const role = user.role || '';
  
  // Business/Admin roles go to admin analytics
  const adminRoles = ['ADMIN', 'HOSPITAL_ADMIN', 'ASC_ADMIN', 'BUSINESS_ADMIN'];
  if (adminRoles.includes(role)) {
    return '/admin/analytics';
  }

  // Clinical roles go to clinical analytics
  const clinicalRoles = ['PHYSICIAN', 'NURSE', 'RESIDENT', 'SURGEON', 'ANESTHESIOLOGIST', 'CLINICIAN', 'TECH'];
  if (clinicalRoles.includes(role)) {
    return '/clinical/analytics';
  }

  // Default to patient summary for other roles
  return '/patient-management';
}

/**
 * Role groups for easy reference
 */
export const ROLE_GROUPS = {
  ADMIN: ['ADMIN', 'HOSPITAL_ADMIN', 'ASC_ADMIN', 'BUSINESS_ADMIN'],
  CLINICAL: ['PHYSICIAN', 'NURSE', 'RESIDENT', 'SURGEON', 'ANESTHESIOLOGIST', 'CLINICIAN', 'TECH'],
  VIEWER: ['VIEWER', 'CLERK'],
  ALL_STAFF: ['ADMIN', 'HOSPITAL_ADMIN', 'ASC_ADMIN', 'BUSINESS_ADMIN', 'PHYSICIAN', 'NURSE', 'RESIDENT', 'SURGEON', 'ANESTHESIOLOGIST', 'CLINICIAN', 'TECH', 'VIEWER', 'CLERK'],
};
